import React from "react";
import { NavLink } from "react-router-dom";

/**
 * Sidebar
 * - Nav links stay packed at the top.
 * - Footer sticks to the bottom of the sidebar column (not the page).
 * - Works in expanded/collapsed modes (controlled by parent).
 * - On mobile (drawer), shows a small header with a close button.
 * - If an `onClose` prop is provided, clicking any nav link will close the drawer.
 */
export default function Sidebar({ me, open = true, onSignOut, onClose }) {
  const isMobile = typeof window !== "undefined" ? window.innerWidth < 768 : false;

  // ----- Robust role detection (email-domain aware) -----
  const email = (me?.email || me?.username || "").trim().toLowerCase();
  const username = (me?.username || "").trim().toLowerCase();
  const isStudentDomain =
    /@student\.propelschools\.org$/i.test(email) ||
    /@student\.propelschools\.org$/i.test(username);

  const backendRole = String(me?.role || "").trim().toUpperCase();

  // Educator role model:
  // - Only two user types: Educator and Scholar
  // - Admin is a kind of Educator (ADMIN ⊂ EDUCATOR)
  // So ADMIN must be treated as educator for UI purposes.
  const educatorRole =
    backendRole === "EDUCATOR" ||
    backendRole === "ADMIN" ||
    backendRole === "STAFF" ||
    backendRole === "TEACHER";

  const educatorHints = !!(
    educatorRole ||
    me?.is_superuser ||
    me?.is_staff ||
    me?.educator_profile ||
    me?.educator ||
    me?.teacher_profile
  );

  // Student-domain accounts should never see educator nav (safety net)
  const isEducator = educatorHints && !isStudentDomain;

  // ===== derived user info =====
  const displayName =
    me?.full_name ||
    [me?.first_name, me?.last_name].filter(Boolean).join(" ") ||
    me?.username ||
    me?.email ||
    "User";

  const displayEmail = me?.email;

  const initials =
    (displayName || "")
      .split(/\s+/)
      .filter(Boolean)
      .map((s) => s[0]?.toUpperCase())
      .slice(0, 2)
      .join("") || "U";

  // Only superusers/staff should see the Admin Portal link
  const canSeeAdmin = !!(me?.is_superuser || me?.is_staff || backendRole === "ADMIN");

  const handleNavClick = () => {
    if (isMobile && typeof onClose === "function") onClose();
  };

  // ===== styles =====
  const linkBase = {
    display: "grid",
    gridTemplateColumns: open ? "24px 1fr" : "24px",
    alignItems: "center",
    gap: 10,
    padding: "10px 12px",
    borderRadius: 10,
    textDecoration: "none",
    color: "#042d60",
    fontWeight: 700,
    border: "1px solid transparent",
  };

  const active = { background: "#E6F1FA", borderColor: "#89c3e8" };
  const iconCell = { fontSize: 18, width: 24, textAlign: "center" };

  const mobileHeader = {
    display: isMobile ? "flex" : "none",
    alignItems: "center",
    justifyContent: open ? "space-between" : "flex-end",
    gap: 8,
    padding: "10px 8px",
    height: 48,
    borderBottom: "1px solid rgba(0,0,0,0.06)",
    background: "#ffffff",
    position: "sticky",
    top: 0,
    zIndex: 1,
  };

  const closeBtn = {
    width: 36,
    height: 36,
    borderRadius: 10,
    border: "1px solid #dae7f2",
    background: "#fff",
    color: "#003e73",
    fontWeight: 900,
    cursor: "pointer",
  };

  const mobileTitle = { display: open ? "block" : "none", fontWeight: 900, color: "#003e73" };

  return (
    <div style={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
      {/* Mobile drawer header with close button */}
      <div style={mobileHeader}>
        <div style={mobileTitle}>Propel Points</div>
        <button
          type="button"
          aria-label="Close menu"
          title="Close menu"
          onClick={onClose}
          style={closeBtn}
        >
          ×
        </button>
      </div>

      {/* Primary nav */}
      <nav style={{ display: "grid", gap: 6, padding: 8, alignContent: "start" }}>
        {/* Everyone: Dashboard */}
        <NavLink
          to="/dashboard"
          title="Dashboard"
          onClick={handleNavClick}
          style={({ isActive }) => ({ ...linkBase, ...(isActive ? active : {}) })}
        >
          <span style={iconCell} aria-hidden>🏠</span>
          {open && <span>Dashboard</span>}
        </NavLink>

        {/* Educators only */}
        {isEducator && (
          <NavLink
            to="/award"
            title="Award Points"
            onClick={handleNavClick}
            style={({ isActive }) => ({ ...linkBase, ...(isActive ? active : {}) })}
          >
            <span style={iconCell} aria-hidden>⭐</span>
            {open && <span>Award Points</span>}
          </NavLink>
        )}

        {isEducator && (
          <NavLink
            to="/rosters"
            title="Rosters"
            onClick={handleNavClick}
            style={({ isActive }) => ({ ...linkBase, ...(isActive ? active : {}) })}
          >
            <span style={iconCell} aria-hidden>👥</span>
            {open && <span>Rosters</span>}
          </NavLink>
        )}

        {isEducator && (
          <NavLink
            to="/reports"
            title="Reports"
            onClick={handleNavClick}
            style={({ isActive }) => ({ ...linkBase, ...(isActive ? active : {}) })}
          >
            <span style={iconCell} aria-hidden>📊</span>
            {open && <span>Reports</span>}
          </NavLink>
        )}

        {/* Scholars: My Points */}
        {!isEducator && (
          <NavLink
            to="/my-points"
            title="My Points"
            onClick={handleNavClick}
            style={({ isActive }) => ({ ...linkBase, ...(isActive ? active : {}) })}
          >
            <span style={iconCell} aria-hidden>🧾</span>
            {open && <span>My Points</span>}
          </NavLink>
        )}

        {/* Everyone: Store */}
        <NavLink
          to="/store"
          title="Store"
          onClick={handleNavClick}
          style={({ isActive }) => ({ ...linkBase, ...(isActive ? active : {}) })}
        >
          <span style={iconCell} aria-hidden>🛍️</span>
          {open && <span>Store</span>}
        </NavLink>
      </nav>

      {/* Footer */}
      <div
        style={{
          display: "grid",
          gap: 8,
          padding: 8,
          borderTop: "1px solid rgba(0,0,0,0.06)",
          marginTop: "auto",
          background: "#ffffff",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: open ? "36px 1fr" : "36px",
            alignItems: "center",
            gap: 8,
          }}
        >
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              display: "grid",
              placeItems: "center",
              fontWeight: 900,
              color: "#003e73",
              border: "1px solid #dae7f2",
              background: "#fff",
            }}
            aria-hidden
          >
            {initials}
          </div>
          {open && (
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 800, color: "#003e73", lineHeight: 1.1 }}>{displayName}</div>
              {displayEmail ? (
                <div
                  style={{
                    fontSize: 12,
                    color: "#466",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {displayEmail}
                </div>
              ) : null}
            </div>
          )}
        </div>

        {/* Admin portal link — visible only to superusers/staff/admin-role */}
        {canSeeAdmin && (
          <a
            href="https://api.propelhabitpoints.org/admin/"
            target="_blank"
            rel="noreferrer"
            style={{ textDecoration: "none", color: "#003e73", fontWeight: 800 }}
            title="Open Admin Portal"
            onClick={handleNavClick}
          >
            {open ? "Admin Portal ↗" : "🗂️"}
          </a>
        )}

        <button
          type="button"
          onClick={onSignOut}
          style={{
            height: 40,
            borderRadius: 999,
            border: "1px solid #dae7f2",
            background: "#fff",
            color: "#003e73",
            fontWeight: 800,
            cursor: "pointer",
          }}
          title="Sign out"
        >
          {open ? "Sign out" : "⎋"}
        </button>
      </div>
    </div>
  );
}
